import { Injectable } from '@angular/core';
import {
	ExternalTransferApiService,
	ExternalTransferRequestDto,
	ExternalTransferResponseDto
} from '@api/external-transfer';
import { AuthProvider } from '@domain/authentication';
import { ErrorCodes, FileModel, ToastDuration, ToastService, lowercaseKeys } from '@shared';
import { firstValueFrom, map } from 'rxjs';
import { TransferOutStragegy as TransferOutStrategy } from './transfer-out.strategy';
import { RegistrationHandler } from '../../../../domain/json-file-importer/import-process/registration';
import { FileValidatorHandler } from '../../../../domain/json-file-importer/import-process/validation';
import { LoggingService } from '@shared/logging';
import { DeviceIdProvider } from '@database/workspace';
import { JsonFileDataParser } from '@database/central';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class DataAndStructureTransferOutStrategy extends TransferOutStrategy {
	public constructor(
		private externalTransferApiService: ExternalTransferApiService,
		private authProvider: AuthProvider,
		private loggingService: LoggingService,
		private deviceIdProvider: DeviceIdProvider,
		private jsonFileDataParser: JsonFileDataParser,
		private toastService: ToastService,
		private translateService: TranslateService
	) {
		super();
	}

	public async validate(connectionId: string, environment: string, externalTransferId: string): Promise<FileModel> {
		const authMethod = await this.authProvider.getAuthenticationMethod();

		let workfile: FileModel;

		try {
			workfile = await firstValueFrom(
				this.externalTransferApiService
					.getExternalTransfer$(
						connectionId,
						new ExternalTransferRequestDto({
							AuthenticationMethod: authMethod,
							Environment: environment,
							ExternalTransferId: externalTransferId
						})
					)
					.pipe(map((response: ExternalTransferResponseDto) => JSON.parse(response.Data)))
			);
			workfile.TABLE_DATA = lowercaseKeys(workfile.TABLE_DATA)
		} catch (error) {
			this.loggingService.error(ErrorCodes.TransferOutDataStructureError, error.errorMessage);
			await this.toastService.presentError(error.errorMessage, ToastDuration.Fast);
			throw new Error(ErrorCodes.TransferOutDataStructureError);
		}

		// 1. registration handler
		const registrationHandler = new RegistrationHandler(this.loggingService, this.deviceIdProvider);

		// 2. validate file handler
		const fileValidator = new FileValidatorHandler(
			this.loggingService,
			this.jsonFileDataParser,
			this.toastService,
			this.translateService
		);

		registrationHandler.setNext(fileValidator);
		await registrationHandler.handle(workfile);

		return workfile;
	}
}
